import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuscarClientesRow } from '@core/types';
import { buscarClientes, criarCliente } from '../lib/consultas';
import { formatarDocumento, formatarTelefone, problemaNoCliente } from '../lib/cliente';
import { dataBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import Aviso from './Aviso';

// O cliente da venda (migração 023). Dois gestos, um componente:
//   ACHAR   — busca por nome sujo, CPF ou telefone; a lista mostra quantas
//             compras a pessoa já fez, que é o que distingue dois "João Silva".
//   CRIAR   — nome, CPF e telefone, sem sair da tela de venda. É o cadastro
//             just-in-time da peça aplicado a gente: se exigir ir a outra tela,
//             o vendedor fecha a venda sem cliente e o histórico não existe.
//
// Só o NOME é obrigatório. Quem valida CPF e telefone é lib/cliente.ts, e a
// validação acontece aqui — no banco fica a régua que ninguém contorna, mas é
// aqui que dá para explicar o erro enquanto a pessoa digita.

export default function SeletorCliente({
  clienteId, clienteNome, aoEscolher, rotulo = 'Cliente (opcional)', autoFocus = false,
}: {
  clienteId: string | null;
  /** Nome digitado à mão quando não há cadastro — o campo antigo, que continua
   *  valendo: nem toda venda precisa de cliente cadastrado. */
  clienteNome: string;
  /** `telefone` vem junto quando a escolha saiu do cadastro: a tela que tem um
   *  campo de contato (a encomenda) preenche o dela sem obrigar a redigitar. */
  aoEscolher: (v: { id: string | null; nome: string; telefone?: string | null }) => void;
  /** O rótulo do campo. Nem toda tela trata o cliente como opcional — na
   *  encomenda ele é obrigatório, e o rótulo precisa dizer isso. */
  rotulo?: string;
  /** Focar ao montar. A encomenda faz isso desde sempre: o campo nasce junto
   *  com a primeira peça da lista, e o cursor já espera o nome do cliente. */
  autoFocus?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [criando, setCriando] = useState(false);
  // O termo debounced que alimenta a lista quando a busca está aberta. Mora
  // AQUI, e não dentro da lista, porque o campo é um só (12/08/2026): antes o
  // painel de busca abria com um segundo input próprio, e ficavam duas caixas
  // de pesquisa empilhadas — quem digitava na de cima não via resultado.
  const [termo, setTermo] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTermo(clienteNome.trim()), 250);
    return () => clearTimeout(t);
  }, [clienteNome]);

  if (clienteId) {
    return (
      <ClienteEscolhido
        id={clienteId}
        nome={clienteNome}
        aoTrocar={() => { aoEscolher({ id: null, nome: '' }); setAberto(true); }}
      />
    );
  }

  return (
    <div>
      <label className="rotulo" htmlFor="cliente">{rotulo}</label>
      <div className="flex gap-2">
        <input
          id="cliente"
          className="campo"
          value={clienteNome}
          autoFocus={autoFocus}
          placeholder={aberto ? 'Nome, CPF ou telefone' : 'Nome, ou toque em Buscar'}
          onChange={(e) => aoEscolher({ id: null, nome: e.target.value })}
          onFocus={() => setAberto(true)}
        />
        <button type="button" className="botao-secundario shrink-0"
                onClick={() => { setAberto((v) => !v); setCriando(false); }}>
          {aberto ? 'Fechar' : 'Buscar'}
        </button>
      </div>

      {aberto && !criando && (
        <BuscaDeCliente
          termo={termo}
          aoEscolher={(c) => {
            aoEscolher({ id: c.id, nome: c.nome, telefone: c.telefone });
            setAberto(false);
          }}
          aoCriarNovo={() => setCriando(true)}
        />
      )}

      {aberto && criando && (
        <CadastroRapido
          nomeInicial={clienteNome}
          aoCancelar={() => setCriando(false)}
          aoCriar={(c) => {
            aoEscolher({ id: c.id, nome: c.nome, telefone: c.telefone });
            setAberto(false); setCriando(false);
          }}
        />
      )}
    </div>
  );
}

function ClienteEscolhido({ id, nome, aoTrocar }: {
  id: string; nome: string; aoTrocar: () => void;
}) {
  // A lista traz CPF e telefone; buscar de novo pelo nome exato é barato e
  // evita carregar o cadastro inteiro só para mostrar duas linhas.
  const { data } = useQuery({
    queryKey: ['cliente-escolhido', id],
    queryFn: () => buscarClientes(nome, 5),
    staleTime: 60_000,
  });
  const c = (data ?? []).find((x) => x.id === id);

  return (
    <div>
      <p className="rotulo">Cliente</p>
      <div className="flex items-center justify-between gap-3 border border-signal/50 bg-signal/5 p-3">
        <div className="min-w-0">
          <p className="truncate font-bold">{nome}</p>
          <p className="text-xs text-mute">
            {c?.cpf ? formatarDocumento(c.cpf) : 'sem CPF'}
            {c?.telefone ? ` · ${formatarTelefone(c.telefone)}` : ''}
            {c && c.compras > 0 ? ` · ${c.compras} compra(s)` : ''}
          </p>
        </div>
        <button type="button" className="shrink-0 px-2 text-sm text-erro" onClick={aoTrocar}>
          trocar
        </button>
      </div>
    </div>
  );
}

function BuscaDeCliente({ termo, aoEscolher, aoCriarNovo }: {
  /** Vem do ÚNICO campo, no pai — este painel é só a lista de resultados. */
  termo: string;
  aoEscolher: (c: BuscarClientesRow) => void;
  aoCriarNovo: () => void;
}) {
  const { data, isFetching, error } = useQuery({
    queryKey: ['clientes', termo],
    queryFn: () => buscarClientes(termo),
    staleTime: 30_000,
  });

  return (
    <div className="mt-2 border border-edge p-3">
      {error ? <div><Aviso tipo="erro">{mensagemDeErro(error)}</Aviso></div> : null}
      {isFetching && <p className="text-xs text-dim">Procurando…</p>}
      {termo === '' && !isFetching && (
        <p className="text-xs text-mute">Digite no campo acima para procurar.</p>
      )}

      {data && data.length > 0 && (
        <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
          {data.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => aoEscolher(c)}
                className="w-full border border-edge p-2 text-left hover:border-signal"
              >
                <p className="truncate text-sm font-semibold">{c.nome}</p>
                <p className="text-xs text-dim">
                  {c.cpf ? formatarDocumento(c.cpf) : 'sem CPF'}
                  {c.telefone ? ` · ${formatarTelefone(c.telefone)}` : ''}
                  {/* A contagem é o que separa dois homônimos na lista. */}
                  {c.compras > 0
                    ? ` · ${c.compras} compra(s)${c.ultima_compra ? `, última ${dataBR(c.ultima_compra)}` : ''}`
                    : ' · sem compras'}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}

      {data && data.length === 0 && !isFetching && (
        <p className="mt-2 text-xs text-mute">Nenhum cliente encontrado para "{termo}".</p>
      )}

      <button type="button" className="botao-secundario mt-3 w-full py-2 text-sm" onClick={aoCriarNovo}>
        + Cadastrar cliente novo
      </button>
    </div>
  );
}

function CadastroRapido({ nomeInicial, aoCriar, aoCancelar }: {
  nomeInicial: string;
  aoCriar: (c: { id: string; nome: string; telefone: string | null }) => void;
  aoCancelar: () => void;
}) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState(nomeInicial);
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');

  const criar = useMutation({
    mutationFn: async () => {
      const problema = problemaNoCliente({ nome, cpf, telefone });
      if (problema) throw new Error(problema);
      return criarCliente({ nome, cpf, telefone });
    },
    onSuccess: (c) => {
      cliente.invalidateQueries({ queryKey: ['clientes'] });
      aoCriar({ id: c.id, nome: c.nome, telefone: c.telefone });
    },
  });

  return (
    <div className="mt-2 space-y-3 border border-signal/50 p-3">
      <p className="rotulo">Cliente novo</p>
      <div>
        <label className="rotulo" htmlFor="nc-nome">Nome *</label>
        <input id="nc-nome" className="campo py-2" value={nome} autoFocus
               onChange={(e) => setNome(e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="nc-cpf">CPF / CNPJ</label>
          <input id="nc-cpf" className="campo py-2 font-mono" inputMode="numeric" value={cpf}
                 placeholder="000.000.000-00"
                 onChange={(e) => setCpf(e.target.value)}
                 onBlur={() => setCpf(formatarDocumento(cpf))} />
        </div>
        <div>
          <label className="rotulo" htmlFor="nc-fone">Telefone</label>
          <input id="nc-fone" className="campo py-2" inputMode="tel" value={telefone}
                 placeholder="(53) 99988-7766"
                 onChange={(e) => setTelefone(e.target.value)}
                 onBlur={() => setTelefone(formatarTelefone(telefone))} />
        </div>
      </div>
      <p className="text-xs text-dim">
        Só o nome é obrigatório — dá para completar depois. CPF, quando vier, não pode repetir:
        é ele que mantém o histórico do cliente num lugar só.
      </p>

      {criar.error ? <Aviso tipo="erro">{mensagemDeErro(criar.error)}</Aviso> : null}

      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoCancelar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm"
                disabled={criar.isPending} onClick={() => criar.mutate()}>
          {criar.isPending ? 'Salvando…' : 'Cadastrar e usar'}
        </button>
      </div>
    </div>
  );
}
